import React, { useState, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import CounselorCard from './CounselorCard';
import CounselorProfileModal from './CounselorProfileModal';
import Header from '../Header';
import styles from './CounselorConnect.module.css';
import { Search, ArrowRight } from 'lucide-react';

// Mock data
const mockCounselors = [
  {
    id: 1,
    name: "Dr. Sarah Chen",
    title: "College Admissions Expert",
    profileImage: "https://i.pravatar.cc/150?img=1",
    experience: "5+ years",
    specialties: ["All Specialties", "Ivy League", "Medical School"],
    location: "San Francisco, CA",
    hourlyRate: 150,
    education: "Harvard University B.A.",
    bookingUrl: "#"
  },
  {
    id: 2,
    name: "Jude Bellingham",
    title: "Career Development",
    profileImage: "https://i.pravatar.cc/150?img=2",
    experience: "2+ years",
    specialties: ["Career Prep", "Ivy League", "Medical School"],
    location: "Madrid, ESP",
    hourlyRate: 70,
    education: "Stanford University M.Ed.",
    bookingUrl: "#"
  },
  {
    id: 3,
    name: "Robert Lewandowski",
    title: "Graduate Counselor Advisor",
    profileImage: "https://i.pravatar.cc/150?img=3",
    experience: "5+ years",
    specialties: ["All Specialties", "Ivy League", "Medical School"],
    location: "Barcelona, ESP",
    hourlyRate: 110,
    education: "Yale University Ph.D.",
    bookingUrl: "#"
  }
];

const specialtyFilters = [
  "All Specialties",
  "Ivy League",
  "STEM Applications",
  "Essay Review",
  "Career Prep",
  "Medical School"
];

const CounselorConnect = () => {
  const [activeFilter, setActiveFilter] = useState("All Specialties");
  const [selectedCounselor, setSelectedCounselor] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const location = useLocation();

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedCounselor) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [selectedCounselor]);

  // Filter counselors based on active filter and search query
  const filteredCounselors = useMemo(() => {
    let filtered = mockCounselors;

    // Apply specialty filter
    if (activeFilter !== "All Specialties") {
      filtered = filtered.filter(counselor =>
        counselor.specialties.includes(activeFilter)
      );
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(counselor =>
        counselor.name.toLowerCase().includes(query) ||
        counselor.title.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [activeFilter, searchQuery]);

  const handleFilterClick = (filter) => {
    setActiveFilter(filter);
  };

  const handleCardClick = (counselor) => {
    setSelectedCounselor(counselor);
  };

  const handleCloseModal = () => {
    setSelectedCounselor(null);
  };

  return (
    <div className={styles.pageContainer}>
      <Header 
        onMenuClick={() => {}} 
        onProfileClick={() => {}} 
      />
      <div className={styles.container}>
        <div className={styles.filterBar}>
          <div className={styles.searchContainer}>
            <Search className={styles.searchIcon} size={20} />
            <input
              type="text"
              placeholder="Search"
              className={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className={styles.filtersContainer}>
            <div className={styles.filterPills}>
              {specialtyFilters.map((filter) => (
                <button
                  key={filter}
                  className={`${styles.filterPill} ${
                    activeFilter === filter ? styles.filterPillActive : ''
                  }`}
                  onClick={() => handleFilterClick(filter)}
                >
                  {filter}
                </button>
              ))}
            </div>
            <button className={styles.arrowButton}>
              <ArrowRight size={20} />
            </button>
          </div>
        </div>

        <div className={styles.counselorsGrid}>
          {filteredCounselors.length > 0 ? (
            filteredCounselors.map((counselor) => (
              <CounselorCard
                key={counselor.id}
                counselor={counselor}
                onClick={() => handleCardClick(counselor)}
              />
            ))
          ) : (
            <div className={styles.noResults}>
              <p>No counselors found matching your criteria.</p>
            </div>
          )}
        </div>
      </div>

      {selectedCounselor && (
        <CounselorProfileModal
          counselor={selectedCounselor}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default CounselorConnect;

